import JSZip from "jszip";

const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export { DOCX_MIME };

function escapeXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Pull paragraph-ish lines from live editor HTML. */
export function paragraphsFromHtml(html: string): string[] {
  if (typeof document === "undefined") {
    return html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .split(/\n+/)
      .map((l) => l.replace(/\u00a0/g, " ").trim())
      .filter(Boolean);
  }
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  tmp.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
  const blocks = Array.from(
    tmp.querySelectorAll("p, div, li, h1, h2, h3, h4, h5, h6"),
  );
  if (blocks.length) {
    return blocks
      .map((el) => (el.textContent || "").replace(/\u00a0/g, " ").trim())
      .filter(Boolean);
  }
  return (tmp.innerText || tmp.textContent || "")
    .split(/\n+/)
    .map((l) => l.replace(/\u00a0/g, " ").trim())
    .filter(Boolean);
}

export function extractLiveDocxHtml(root: HTMLElement | null): string {
  if (!root) return "";
  const editable =
    (root.querySelector(
      '.docx-editable[contenteditable="true"]',
    ) as HTMLElement | null) ??
    (root.querySelector("[contenteditable='true']") as HTMLElement | null) ??
    root;
  return editable.innerHTML || "";
}

/** Minimal RTL Word (.docx) package from plain text / editor HTML. */
export async function buildSimpleDocxBytes(opts: {
  text?: string;
  html?: string;
}): Promise<Uint8Array> {
  let paras: string[] = [];
  if (opts.html?.trim()) {
    paras = paragraphsFromHtml(opts.html);
  }
  if (!paras.length && opts.text?.trim()) {
    paras = opts.text
      .split(/\n+/)
      .map((l) => l.replace(/\u00a0/g, " ").trim())
      .filter(Boolean);
  }
  if (!paras.length) paras = [""];

  const body = paras
    .map(
      (p) =>
        `<w:p><w:pPr><w:bidi/><w:jc w:val="right"/></w:pPr><w:r><w:rPr><w:rtl/></w:rPr><w:t xml:space="preserve">${escapeXml(p)}</w:t></w:r></w:p>`,
    )
    .join("");

  const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>${body}<w:sectPr><w:pgSz w:w="11906" w:h="16838"/><w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440"/></w:sectPr></w:body>
</w:document>`;

  const zip = new JSZip();
  zip.file(
    "[Content_Types].xml",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
</Types>`,
  );
  zip.folder("_rels")!.file(
    ".rels",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`,
  );
  const word = zip.folder("word")!;
  word.file("document.xml", documentXml);
  word.folder("_rels")!.file(
    "document.xml.rels",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"></Relationships>`,
  );

  return zip.generateAsync({ type: "uint8array" });
}
